const URL = process.env.NEXT_PUBLIC_API_URL || 'https://image-nation.org/api-service';
const REQUEST_TIMEOUT = 30000; // 30 seconds

export function getApiAuthHeaders(accessToken?: string): Record<string, string> {
    return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
}

/**
 * Check if an error is a network error (fetch failure, timeout, CORS, etc.)
 */
export function isNetworkError(error: unknown): boolean {
    if (error instanceof TypeError) {
        // Network errors typically throw TypeError with messages like:
        // - "Failed to fetch"
        // - "NetworkError when attempting to fetch resource"
        // - "Load failed"
        const message = error.message.toLowerCase();
        return (
            message.includes('failed to fetch') ||
            message.includes('networkerror') ||
            message.includes('load failed') ||
            message.includes('network request failed') ||
            message.includes('fetch')
        );
    }
    return false;
}

/**
 * Create a user-friendly error message from a network error
 */
function createNetworkErrorMessage(error: unknown): string {
    if (isNetworkError(error)) {
        return 'Network error: Unable to connect to the server. Please check your internet connection and try again.';
    }
    if (error instanceof Error) {
        return error.message;
    }
    return 'An unexpected error occurred. Please try again.';
}

export async function postData(endpoint: string, data: any, locale?: string) {
    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

        const response = await fetch(URL + endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept-Language': locale || 'en',
            },
            body: JSON.stringify(data),
            signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // Check if response is JSON before parsing
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            throw new Error('Invalid response format from server');
        }

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.message || 'An error occurred');
        }

        return result;
    } catch (error) {
        console.error('Error in postData:', error);

        // Handle abort (timeout)
        if (error instanceof Error && error.name === 'AbortError') {
            throw new Error('Request timed out. Please try again.');
        }

        // Handle network errors with user-friendly messages
        if (isNetworkError(error)) {
            throw new Error(createNetworkErrorMessage(error));
        }

        throw error;
    }
}

export async function getData(endpoint: string, locale?: string, accessToken?: string) {
    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

        const response = await fetch(URL + endpoint, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept-Language': locale || 'en',
                ...getApiAuthHeaders(accessToken),
            },
            signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // Check if response is JSON before parsing
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            throw new Error('Invalid response format from server');
        }

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.message || 'An error occurred');
        }

        return result;
    } catch (error) {
        console.error('Error in getData:', error);

        // Handle abort (timeout)
        if (error instanceof Error && error.name === 'AbortError') {
            throw new Error('Request timed out. Please try again.');
        }

        // Handle network errors with user-friendly messages
        if (isNetworkError(error)) {
            throw new Error(createNetworkErrorMessage(error));
        }

        throw error;
    }
}
